import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@components/common'
import {
  CoversheetCustomFieldType,
  CoversheetFieldSource,
} from '@dataTypes/fax.type'
import {
  COVERSHEET_FIELD_OPTIONS,
  COVERSHEET_FIELD_SOURCE_OPTIONS,
} from '@constants/Message'

type Props = {
  show: boolean
  onHide: () => void
  onInsert: (
    base: CoversheetCustomFieldType,
    source: CoversheetFieldSource,
  ) => void
}

const InsertFieldModal: React.FC<Props> = ({ show, onHide, onInsert }) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const [base, setBase] = useState<CoversheetCustomFieldType | null>(null)
  const [source, setSource] = useState<CoversheetFieldSource>('custom')

  useEffect(() => {
    if (!show) return
    setBase(null)
    setSource('custom')
  }, [show])

  useEffect(() => {
    if (!show) return undefined

    const handleClickAway = (event: MouseEvent): void => {
      const target = event.target as Node | null
      if (!target) return
      if (panelRef.current?.contains(target)) return
      // Let the trigger button toggle open/close without also closing here
      if (target instanceof Element && target.closest('.add-field-button')) {
        return
      }
      onHide()
    }

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onHide()
    }

    document.addEventListener('mousedown', handleClickAway)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickAway)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [show, onHide])

  if (!show) return null

  const sourceOptions = base ? COVERSHEET_FIELD_SOURCE_OPTIONS[base] : []

  const handleSelectBase = (next: CoversheetCustomFieldType): void => {
    setBase(next)
    setSource('custom')
  }

  const handleInsert = (): void => {
    if (!base) return
    onInsert(base, source)
  }

  return (
    <div
      ref={panelRef}
      className="fax-insert-field-popover"
      role="dialog"
      aria-label="Insert new fields"
    >
      <div className="fax-field-section">
        <div className="fax-field-section-title">Select field</div>
        <div className="fax-field-option-list">
          {COVERSHEET_FIELD_OPTIONS.map((opt) => (
            <label key={opt.id} className="fax-field-option">
              <input
                type="radio"
                name="fieldType"
                checked={base === opt.id}
                onChange={() => handleSelectBase(opt.id)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {sourceOptions.length > 0 && (
        <div className="fax-field-section">
          <div className="fax-field-section-title">Auto-filled data fields</div>
          <div className="fax-field-option-list">
            {sourceOptions.map((opt) => (
              <label key={opt.id} className="fax-field-option">
                <input
                  type="radio"
                  name="fieldSource"
                  checked={source === opt.id}
                  onChange={() => setSource(opt.id)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="fax-insert-field-popover-actions">
        <Button className="button-secondary" size="sm" onClick={onHide}>
          Cancel
        </Button>
        <Button
          className="button-primary"
          size="sm"
          disabled={!base}
          onClick={handleInsert}
        >
          Insert field
        </Button>
      </div>
    </div>
  )
}

export default InsertFieldModal
